import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { sodexAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { normalizeUserAddress } from '../normalizeUserAddress'
import { resolveCoinAsset } from '../resolveCoinAsset'
import { responseSchema } from './schema'

/**
 * 先物約定履歴 API から Action 一覧を取得する（手数料のみ）
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })
  const userAddress = normalizeUserAddress({ address: apiKey })

  const client = createClient({
    market: 'perps',
    createHeaders,
    proxy,
  })

  const query: Record<string, string> = {
    limit: config.limit.toString(),
    ...(from ? { startTime: from.toString() } : {}),
    ...(to ? { endTime: to.toString() } : {}),
  }

  const trades =
    (await client.run({
      method: 'GET',
      path: `/accounts/${userAddress}/trades`,
      query,
      responseSchema,
    })) ?? []

  const sortedTrades = trades.toSorted((a, b) => a.time - b.time)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const trade of sortedTrades) {
    fetchedFrom =
      fetchedFrom === undefined ? trade.time : Math.min(fetchedFrom, trade.time)
    fetchedTo =
      fetchedTo === undefined ? trade.time : Math.max(fetchedTo, trade.time)

    if (!trade.fee || !trade.feeCoin || trade.fee.value === 0n) {
      continue
    }

    const feeAsset = resolveCoinAsset({ coin: trade.feeCoin, getAsset })
    if (!feeAsset) {
      throw new AssetNotFoundException(trade.feeCoin)
    }

    actions.push({
      comment: `Trade Fee of ${trade.symbol}/${trade.feeCoin}`,
      evidence: 'system-rule',
      order: 0,
      source: `${sodexAccountCategory}_${config.type}_${trade.tradeID}_${trade.time}`,
      type: trade.fee.value > 0n ? 'fee' : 'income',
      timestamp: trade.time,
      transfers: [
        {
          asset: feeAsset,
          amount: absoluteValue(trade.fee),
          direction: trade.fee.value > 0n ? 'out' : 'in',
        },
      ],
    })
  }

  const sortedActions = actions.toSorted((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: trades.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
